import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, tap } from 'rxjs';
import { CustomerInfoService } from '../../features/customer-info/services/customer-info.service';
import { CustomerInfo } from '../../features/customer-info/model/customer-info';
import { CustomerSetupResponse } from 'src/app/features/customer/models/response.model';

export class UpdateCurrentCustomerAction {
  static readonly type = '[Customer Info] Update Current Customer';
  constructor(public dealerId: string, public customerCode: string) {}
}

export class UpdateCustomerInfoAction {
  static readonly type = '[Customer Info] Update Customer Info';
  constructor(public customerSetupResponse: CustomerSetupResponse) {}
}

export class ResetCustomerStateAction {
  static readonly type = '[Customer Info] Customer State Reset';
}

export class CustomerInfoStateModel {
  loading = true;
  customerInfo: CustomerInfo = undefined;
}

const defaultState = {
  loading: true,
  customerInfo: undefined as CustomerInfo
};

const getDefaultState = () => {
  return { ...defaultState };
};

@State<CustomerInfoStateModel>({
  name: 'currentCustomer',
  defaults: getDefaultState()
})
@Injectable()
export class CustomerInfoState {
  public customerInfoService = inject(CustomerInfoService);
  constructor() {}

  @Action(UpdateCurrentCustomerAction)
  setCurrentCustomer({ patchState }: StateContext<CustomerInfoStateModel>, { dealerId, customerCode }: UpdateCurrentCustomerAction) {
    patchState({ loading: true });
    return this.customerInfoService.getCustomerInfo(dealerId, customerCode).pipe(
      tap((customerInfoResponse) => {
        patchState({ customerInfo: customerInfoResponse.data, loading: false });
      }),

      catchError((error) => {
        patchState(getDefaultState());
        throw error;
      })
    );
  }

  @Action(UpdateCustomerInfoAction)
  setCustomerInfo({ patchState }: StateContext<CustomerInfoStateModel>, { customerSetupResponse }: UpdateCustomerInfoAction) {
    const customerInfo: CustomerInfo = {
      customerCode: customerSetupResponse.customerInfo.code,
      customerName: customerSetupResponse.customerInfo.customerName,
      shipTo: customerSetupResponse.customerInfo.shipToCode,
      addressOne: customerSetupResponse.customerInfo.address1,
      addressTwo: customerSetupResponse.customerInfo.address2,
      city: customerSetupResponse.customerInfo.city,
      state: customerSetupResponse.customerInfo.stateProvince,
      zip: customerSetupResponse.customerInfo.postalCode,
      salespersonId: customerSetupResponse.customerInfo.salesId,
      customerId: customerSetupResponse.customerInfo.customerPk
    };
    patchState({ customerInfo });
  }

  @Action(ResetCustomerStateAction)
  resetCustomerStateAction({ patchState }: StateContext<CustomerInfoStateModel>) {
    patchState(getDefaultState());
  }

  @Selector()
  static loading(state: CustomerInfoStateModel) {
    return state.loading;
  }

  @Selector()
  static customerInfo(state: CustomerInfoStateModel) {
    return state.customerInfo;
  }

  @Selector()
  static state(state: CustomerInfoStateModel) {
    return state;
  }
}
